import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Lock, Mail, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [locked, setLocked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [shake, setShake] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting || locked) return;
        setSubmitting(true);
        setError("");
        try {
            await login(email.trim(), password);
            navigate("/dashboard", { replace: true });
        } catch (err) {
            const status = err?.response?.status;
            const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Access Denied";
            setError(msg);
            if (status === 423) setLocked(true);
            setShake(true);
            setTimeout(() => setShake(false), 500);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
            data-testid="login-page"
        >
            {/* Backdrop texture */}
            <div
                className="absolute inset-0 opacity-[0.35]"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwb2JzaWRpYW4lMjBncmVlbiUyMGFic3RyYWN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3OTIyMDY5MTF8MA&ixlib=rb-4.1.0&q=85')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(2px) saturate(120%)",
                }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/85 to-[#050505]" />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(600px 300px at 50% 20%, rgba(34,197,94,0.08), transparent 60%)",
                }}
            />

            {/* Terminal header */}
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4 z-10">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#22C55E]/40 rounded-sm">
                        <Leaf size={16} className="text-[#22C55E]" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading text-[#FAFAFA] text-sm font-bold tracking-wider">
                            HOMEGROWN APP
                        </div>
                        <div className="hg-eyebrow">Admin</div>
                    </div>
                </div>
                <div className="hidden sm:flex items-center gap-2 hg-eyebrow">
                    <span className="w-2 h-2 rounded-full bg-[#22C55E] hg-pulse-dot" />
                    Secure Channel
                </div>
            </div>

            {/* Card */}
            <form
                onSubmit={onSubmit}
                className={`hg-glass relative z-10 w-[92%] max-w-[440px] px-8 py-10 rounded-sm hg-fade-up ${
                    shake ? "hg-shake" : ""
                }`}
                data-testid="login-form"
            >
                <div className="hg-eyebrow mb-3">// Restricted Access</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#FAFAFA] leading-none">
                    Sign in to the
                    <br />
                    <span className="text-[#22C55E] hg-glow-sm">command centre</span>
                </h1>
                <p className="mt-3 text-sm text-[#A1A1AA]">
                    Private terminal. Authorised administrators only.
                </p>

                <div className="mt-8 space-y-4">
                    <label className="block">
                        <div className="hg-eyebrow mb-2 flex items-center gap-2">
                            <Mail size={11} /> Admin email
                        </div>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="hg-input"
                            placeholder="admin@homegrownapp.co.nz"
                            autoComplete="username"
                            required
                            disabled={locked}
                            data-testid="login-email-input"
                        />
                    </label>
                    <label className="block">
                        <div className="hg-eyebrow mb-2 flex items-center gap-2">
                            <Lock size={11} /> Passphrase
                        </div>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="hg-input font-mono-data tracking-widest"
                            placeholder="••••••••••"
                            autoComplete="current-password"
                            required
                            disabled={locked}
                            data-testid="login-password-input"
                        />
                    </label>
                </div>

                {error && (
                    <div
                        className="mt-5 flex items-start gap-2 px-3 py-2.5 border border-[#ef4444]/30 bg-[#ef4444]/5 rounded-sm"
                        data-testid="login-error"
                    >
                        <ShieldAlert
                            size={14}
                            className="text-[#ef4444] mt-0.5 shrink-0"
                        />
                        <span className="text-xs text-[#fecaca] font-mono-data">
                            {error}
                        </span>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting || locked}
                    className="hg-btn w-full mt-6"
                    data-testid="login-submit-button"
                >
                    {locked
                        ? "Session locked"
                        : submitting
                        ? "Authenticating…"
                        : "Enter Terminal →"}
                </button>

                <div className="mt-7 flex items-center justify-between hg-eyebrow">
                    <span>// 3 attempts → 10 min lockout</span>
                    <span className="font-mono-data text-[#52525b]">v1.0.0</span>
                </div>
            </form>

            {/* Corner ticks */}
            <CornerTicks />
        </div>
    );
}

const CornerTicks = () => (
    <>
        {["top-6 left-6", "top-6 right-6", "bottom-6 left-6", "bottom-6 right-6"].map(
            (pos) => (
                <div
                    key={pos}
                    className={`absolute ${pos} w-5 h-5 border-[#22C55E]/40 pointer-events-none hidden md:block`}
                    style={{
                        borderTopWidth: pos.includes("top") ? 1 : 0,
                        borderBottomWidth: pos.includes("bottom") ? 1 : 0,
                        borderLeftWidth: pos.includes("left") ? 1 : 0,
                        borderRightWidth: pos.includes("right") ? 1 : 0,
                    }}
                />
            ),
        )}
    </>
);
